import { AddressAutoSearch } from '@src/v3/interfaces/components/searchAutocomplete';
import { ReadonlyURLSearchParams } from 'next/navigation';

/**
 * Get the default search value from URL search params.
 * Parses neighborhood, zip_code, or address_label params into AddressAutoSearch format.
 *
 * @param searchParams - The URL search params object
 * @returns Array of AddressAutoSearch objects
 */
export const getSearchDefaultValue = (
  searchParams: ReadonlyURLSearchParams | null
): AddressAutoSearch[] => {
  const neighborhood = searchParams?.get('neighborhood');
  const zipCode = searchParams?.get('zip_code');
  const addressLabel = searchParams?.get('address_label');

  if (zipCode) {
    const zipCodes = zipCode.split('|');
    return zipCodes.map((zip: string) => ({
      address: zip,
      main_text: zip,
      place_id: zip,
      secondary_text: '',
      types: 'zip_code',
    }));
  }

  if (neighborhood) {
    const neighborhoods = neighborhood.split('|');
    return neighborhoods.map((n: string) => ({
      address: n,
      main_text: n,
      place_id: n,
      secondary_text: '',
      types: 'neighborhood',
    }));
  }

  if (addressLabel) {
    return [
      {
        address: addressLabel,
        main_text: addressLabel,
        place_id: addressLabel,
        secondary_text: '',
        types: 'exact_address',
      },
    ];
  }

  return [];
};

/**
 * Get the default search type from URL search params.
 *
 * @param searchParams - The URL search params object
 * @returns The search type string ('zip_code', 'neighborhood', 'exact_address', or '')
 */
export const getSearchDefaultType = (
  searchParams: ReadonlyURLSearchParams | null
): string => {
  const neighborhood = searchParams?.get('neighborhood');
  const zipCode = searchParams?.get('zip_code');
  const addressLabel = searchParams?.get('exact_address');
  const boundCoordinates = searchParams?.get('bound_coordinates');

  if (zipCode) return 'zip_code';
  if (neighborhood) return 'neighborhood';
  if (addressLabel || boundCoordinates) return 'exact_address';
  return '';
};
